import React from 'react';
import { TrashIcon } from '@heroicons/react/24/solid'; // Using Heroicons for the remove icon

const FixtureInputRow = ({
  fixture,
  options,
  handleFixtureChange,
  handleRemoveFixture,
}) => {
  const { fixtureType, originalIndex } = fixture;

  // Function to get subcategories based on selected category
  const getSubcategories = (optionsArray, category) => {
    if (!Array.isArray(optionsArray)) {
      return [];
    }
    const option = optionsArray.find((opt) => opt.category === category);
    return option ? option.subcategories : [];
  };

  // Find the current category option to determine if dimensions are required
  const currentCategoryOption = options.find((opt) => opt.category === fixture.category);
  const requiresDimensions = currentCategoryOption ? currentCategoryOption.requiresDimensions : false;

  // Handler for input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    handleFixtureChange(fixtureType, originalIndex, name, value);
  };

  return (
    <div className="flex flex-wrap -mx-2 items-center mb-2">
      {/* Category Selection */}
      <div className="w-full md:w-3/12 px-2 mb-2 md:mb-0">
        <label htmlFor={`category-${fixtureType}-${originalIndex}`} className="sr-only">
          Category
        </label>
        <select
          id={`category-${fixtureType}-${originalIndex}`}
          name="category"
          value={fixture.category || ''}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md p-2"
        >
          <option value="" disabled>
            
          </option>
          {options.map((opt, idx) => (
            <option key={`cat-${idx}`} value={opt.category}>
              {opt.category}
            </option>
          ))}
        </select>
      </div>

      {/* Subcategory Selection */}
      <div className="w-full md:w-4/12 px-2 mb-2 md:mb-0">
        <label htmlFor={`subcategory-${fixtureType}-${originalIndex}`} className="sr-only">
          Subcategory
        </label>
        <select
          id={`subcategory-${fixtureType}-${originalIndex}`}
          name="subcategory"
          value={fixture.subcategory || ''}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          disabled={!fixture.category}
        >
          <option value="" disabled>
            
          </option>
          {getSubcategories(options, fixture.category).length > 0 ? (
            getSubcategories(options, fixture.category).map((subcat, idx) => (
              <option key={`subcat-${idx}`} value={subcat}>
                {subcat}
              </option>
            ))
          ) : (
            <option value="" disabled>
              N/A
            </option>
          )}
        </select>
      </div>

      {/* Dimensions Inputs (Only if required) */}
      {requiresDimensions ? (
        <>
          {/* Height Input */}
          <div className="w-full md:w-1/12 px-2 mb-2 md:mb-0">
            <label htmlFor={`height-${fixtureType}-${originalIndex}`} className="sr-only">
              Height
            </label>
            <input
              id={`height-${fixtureType}-${originalIndex}`}
              type="number"
              step="0.01"
              min="0"
              name="dimensions.height"
              value={fixture.dimensions?.height || ''}
              onChange={handleChange}
              placeholder="Height (m)"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Width Input */}
          <div className="w-full md:w-1/12 px-2 mb-2 md:mb-0">
            <label htmlFor={`width-${fixtureType}-${originalIndex}`} className="sr-only">
              Width
            </label>
            <input
              id={`width-${fixtureType}-${originalIndex}`}
              type="number"
              step="0.01"
              min="0"
              name="dimensions.width"
              value={fixture.dimensions?.width || ''}
              onChange={handleChange}
              placeholder="Width (m)"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Depth Input */}
          <div className="w-full md:w-1/12 px-2 mb-2 md:mb-0">
            <label htmlFor={`depth-${fixtureType}-${originalIndex}`} className="sr-only">
              Depth
            </label>
            <input
              id={`depth-${fixtureType}-${originalIndex}`}
              type="number"
              step="0.01"
              min="0"
              name="dimensions.depth"
              value={fixture.dimensions?.depth || ''}
              onChange={handleChange}
              placeholder="Depth (m)"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </div>
        </>
      ) : (
        // Placeholder Divs for Consistent Alignment
        <>
          {/* Height Placeholder */}
          <div className="w-full md:w-1/12 px-2 mb-2 md:mb-0"></div>

          {/* Width Placeholder */}
          <div className="w-full md:w-1/12 px-2 mb-2 md:mb-0"></div>

          {/* Depth Placeholder */}
          <div className="w-full md:w-1/12 px-2 mb-2 md:mb-0"></div>
        </>
      )}

      {/* Quantity Input */}
      <div className={`w-full md:w-1/12 px-2 mb-2 md:mb-0`}>
        <label htmlFor={`quantity-${fixtureType}-${originalIndex}`} className="sr-only">
          Quantity
        </label>
        <input
          id={`quantity-${fixtureType}-${originalIndex}`}
          type="number"
          name="quantity"
          min="1"
          value={fixture.quantity || 1}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md p-2"
        />
      </div>

      {/* Remove Fixture Button */}
      <div className={`w-full md:w-1/12 px-2 flex justify-center md:justify-start`}>
        <button
          onClick={() => handleRemoveFixture(fixtureType, originalIndex)}
          className="mt-1 flex items-center justify-center bg-red-500 text-white p-2 rounded-md hover:bg-red-600 transition-colors"
          aria-label="Remove Fixture"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default React.memo(FixtureInputRow);
